import { useEffect, useMemo, useRef, useState } from "react";
import { DATE_RANGE_PRESETS } from "../lib/dateRangePresets.js";
import { ClusterSwimlane, SurfaceChart, SwimlaneLegend } from "./ChartBlocks.jsx";
import ControlGroup from "./ControlGroup.jsx";
import GridStatus from "./GridStatus.jsx";
import HoverTooltip from "./HoverTooltip.jsx";
import Sheet from "./Sheet.jsx";
import TabStrip from "./TabStrip.jsx";
import { formatCompact, formatUsd } from "../lib/renderers.js";
import { pricedPercent } from "../lib/tokenCost.js";
import { cacheReuseRate } from "../lib/tokenVolume.js";
import KernelWidget from "./KernelWidget.jsx";
import {
    kernelResolverForTenant,
    listKernels,
    resolveKernelByCommandAsync,
    subscribeKernels,
} from "../lib/kernelStore.js";
import {
  RUN_SURFACES,
  createUnknownRunContext,
} from "../lib/kernelResolver.js";
import {
    listPinnedCommands,
    pinKernel,
    unpinKernel,
    subscribePinning,
    scopeKeyOf,
    canMountOnScope,
} from "../lib/kernelPinning.js";
import { connectedServerIds, kernelRequirementsMet } from "../lib/kernelRequirements.js";
import { onConnectionsChanged } from "../lib/mcp/index.js";
import { getCurrentTenantIdSync, useCurrentUser } from "../lib/userInfo.js";
import { SWIMLANE } from "../lib/gridMetrics.js";
import { encodeScopePath } from "../lib/dimensionDefs.js";
import { comparisonsEnabled } from "../lib/featureFlags.js";
import { filterComparisonLensOptions } from "../lib/comparisonLens.js";
import {
    addPin,
    removePin,
    isPinned,
    subscribePins,
} from "../lib/overviewHome.js";

// Current pin state predates cloud source envelopes. Treat opening a pinned
// lens as UNKNOWN until server-backed pin reads can return STORED provenance.
const LEGACY_PIN_RUN_CONTEXT = createUnknownRunContext({
  surface: RUN_SURFACES.CANVAS,
});

// Coherence mode is currently gated to "bundle" — the only flavor the
// analytics-api path can compute without per-endpoint signature transport
// (tenant/scoped pairwise requires array operations the proto doesn't
// expose, and shipping per-endpoint signatures over the wire isn't
// tractable at 50k-endpoint scale). The mode is still threaded through
// the swimlane so when tenant/scoped come back we just reinstate
// CoherenceModeToggle here and the wiring is intact.

const LENS_OPTIONS_DEFAULT = [
  ["endpoints", "Active endpoints"],
  ["activity", "Active sessions"],
  ["prompts", "Prompt volume"],
  ["tokens", "Token volume"],
  ["clusters", "Topic swimlane"],
];

// In an endpoint-scoped view we already know which endpoint we're looking
// at, so "Active endpoints" is the least informative choice for first
// position. Surface "Active sessions" first instead.
const LENS_OPTIONS_ENDPOINT_SCOPE = [
  ["activity", "Active sessions"],
  ["endpoints", "Active endpoints"],
  ["prompts", "Prompt volume"],
  ["tokens", "Token volume"],
  ["clusters", "Topic swimlane"],
];

function pickLensOptions(scopePath) {
  const inEndpointScope = (scopePath || []).some((step) => step?.dim === "endpoints");
  const opts = inEndpointScope ? LENS_OPTIONS_ENDPOINT_SCOPE : LENS_OPTIONS_DEFAULT;
  // The Cluster swimlane is a gated comparison surface — drop the tab when off.
  return filterComparisonLensOptions(opts, comparisonsEnabled());
}

// Natural height for the collapsed swimlane. Anchors rows at ROW_TARGET (the
// contrast grid's typical row size) — using ROW_MAX here would make every
// row hit the maximum, doubling the apparent row height vs. the contrast
// grid alongside it. Adds top + bottom breathing room AND reserves space
// for the toolbar above + legend strip below, so the canvas never overflows
// the container (otherwise the DotGrid clamps rows down and the swimlane
// scrolls).
const SWIMLANE_PAD_TOP = 8;
const SWIMLANE_PAD_BOTTOM = 18;
const SWIMLANE_LEGEND_RESERVE = 28;   // SwimlaneLegend strip below the canvas
const SWIMLANE_TOOLBAR_RESERVE = 24;  // CoherenceModeToggle row above the canvas
const SWIMLANE_NOTE_RESERVE = 22;     // optional siblings-of note above the canvas (cluster scope only)
const SWIMLANE_BASE_HEIGHT =
  SWIMLANE.HEADER_H +
  SWIMLANE.MAX_ROWS * SWIMLANE.ROW_TARGET +
  SWIMLANE_PAD_TOP +
  SWIMLANE_PAD_BOTTOM +
  SWIMLANE_LEGEND_RESERVE +
  SWIMLANE_TOOLBAR_RESERVE;

export default function ActivityChart({
  plotConfig,
  loading = false,
  // Ancestor-scope baselines for the active scope, ordered shallowest → deepest:
  // index 0 is the tenant (`[]`) and the last entry is the immediate parent of
  // the active scope. Each entry: `{ key, label, series, locked? }`. `locked`
  // marks the two invariant baselines (tenant + parent) — their toggles render
  // disabled so the user can't drop below the floor.
  baselines = [],
  // Set of baseline keys currently visible. Controlled when a setter is
  // passed; otherwise falls through to internal state seeded from the
  // `locked` entries (so the default on first paint is the floor).
  activeBaselineKeys: activeBaselineKeysProp,
  onActiveBaselineKeysChange,
  range,
  activityLens,
  onRangeChange,
  onLensChange,
  onHoverCluster,
  onHoverCell,
  onClickCell,
  hoveredCell,
  onToggleExpand,
  traces = [],
  clusters = [],
  palette,
  selectedCluster,
  selectedTrace,
  scopePath,
  // A compiled query pipeline (the compilePipeline result) to render in the
  // chart canvas instead of the active lens — the query bar's "kernel spot".
  pipeline = null,
  expanded = false,
  // Fill the parent's height instead of sizing the canvas to its aspect
  // ratio — same sizing path as `expanded`, but without the fullscreen
  // chrome. Used when the surface is the only thing in its panel (the trace
  // log below is hidden by RBAC), so it doesn't float over dead space.
  fill = false,
  eyebrow = "Activity surface",
  title = "Activity",
  className = "h-full",
  chartMinHeight = "198px",
  chartAspectRatio = "17 / 7",
  // When false (overview pivot is overriding the surface scope), don't
  // auto-snap the lens to the new scope's leading tab — that would churn the
  // global lens and desync the working view. The lens stays where it is.
  autoResetLens = true,
  // True only while showing the pinned home full-screen (the override is
  // active) — drives the ⌂'s active state so a plain ⤢ expand of the home
  // scope doesn't light it.
  inPinMode = false,
  // False where a pinned home makes no sense (the workspace's picker): no pin.
  pinnable = true,
}) {
  const coherenceMode = "bundle";

  // Overview-home ⌂ control. `isHome` (scope-only) tracks whether this scope
  // is the pinned home, but the button only reads as ACTIVE when we're
  // actually in the pinned home view (`inPinMode`) — NOT merely when the
  // surface is `expanded` (a plain in-surface ⤢ expand of the home scope
  // would otherwise light it). Otherwise it's an unlit "pin & view as home"
  // affordance. Click matches the visual: lit (in the home view) → unpin;
  // unlit → pin this scope and activate home mode.
  const homeScopeKey = useMemo(() => encodeScopePath(scopePath || []), [scopePath]);
  const [isHome, setIsHome] = useState(() => isPinned(homeScopeKey));
  useEffect(() => {
    const refresh = () => setIsHome(isPinned(homeScopeKey));
    refresh();
    return subscribePins(refresh);
  }, [homeScopeKey]);
  const homeActive = isHome && inPinMode;
  const onHomeClick = () => {
    if (homeActive) {
      removePin(homeScopeKey);
    } else {
      // addPin is a no-op if this scope is already pinned; either way we ask
      // App to flip to it full-screen — see the origin-activate-home listener.
      addPin({ scope: homeScopeKey, lens: activityLens });
      window.dispatchEvent(
        new CustomEvent("origin-activate-home", { detail: { scope: homeScopeKey } }),
      );
    }
  };

  // Baseline-visibility state, tracked as the *hidden* set rather than the
  // active set. Default: nothing hidden → every available baseline visible.
  // This shape is what makes "new baseline appears mid-session" do the
  // sensible thing: if its key isn't in `hiddenBaselineKeys` it shows up
  // automatically (no special "have I seen this key before" bookkeeping).
  // It also lets the user clear every baseline — `hiddenBaselineKeys`
  // simply grows to cover all available keys, including tenant + parent.
  const availableKeys = useMemo(
    () => (baselines || []).map((b) => b.key),
    [baselines],
  );
  const [hiddenBaselineKeysInternal, setHiddenBaselineKeysInternal] = useState(
    () => new Set(),
  );
  const hiddenBaselineKeys = hiddenBaselineKeysInternal;
  const setHiddenBaselineKeys = setHiddenBaselineKeysInternal;

  const activeBaselineKeys = useMemo(() => {
    const s = new Set();
    for (const k of availableKeys) if (!hiddenBaselineKeys.has(k)) s.add(k);
    return s;
  }, [availableKeys, hiddenBaselineKeys]);

  // Controlled-prop bridge: if the caller passes `activeBaselineKeys`, it
  // wins and the internal hidden-set is bypassed entirely.
  const effectiveActiveBaselineKeys =
    activeBaselineKeysProp !== undefined ? activeBaselineKeysProp : activeBaselineKeys;

  const toggleBaseline = (key) => {
    if (onActiveBaselineKeysChange) {
      const next = new Set(effectiveActiveBaselineKeys);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      onActiveBaselineKeysChange(next);
      return;
    }
    setHiddenBaselineKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Visible-baseline list passed to the renderer. Styled per-entry so
  // multiple lines stay distinguishable: deeper ancestors render slightly
  // more opaque, and any non-parent ancestors render dashed so the eye
  // can tell them apart from the parent without colour.
  const visibleBaselines = useMemo(() => {
    const list = (baselines || []).filter((b) => effectiveActiveBaselineKeys.has(b.key));
    const lastIndex = list.length - 1;
    return list.map((b, i) => {
      const isParent = i === lastIndex; // ordered shallowest → deepest
      // Opacity ramps from 0.28 (tenant) → 0.55 (parent) so the parent
      // reads as the dominant reference. The ramp stays bounded for
      // edge cases (0 or 1 entry) so a single baseline still reads OK.
      const t = lastIndex === 0 ? 1 : i / lastIndex;
      const strokeOpacity = 0.28 + (0.55 - 0.28) * t;
      return {
        key: b.key,
        label: b.label,
        series: b.series,
        strokeOpacity,
        // Parent stays solid (familiar to long-time users — single
        // reference was always solid); ancestors above it get a long
        // dash so the chart legibly stacks 2-3 lines without colour.
        lineDash: isParent ? null : [6, 4],
      };
    });
  }, [baselines, effectiveActiveBaselineKeys]);
  // Server-reported swimlane metadata: `siblingsOf` for the cluster-scope
  // sibling note, and `error` so we can show the user a non-zero-state
  // banner if the swimlane query failed. Bubbled up by ClusterSwimlane via
  // onMetaChange.
  const [swimlaneMeta, setSwimlaneMeta] = useState(null);
  const siblingsOf = swimlaneMeta?.siblingsOf || null;
  const swimlaneError = swimlaneMeta?.error || null;
  const lensOptions = useMemo(() => pickLensOptions(scopePath), [scopePath]);
  // When the tab order flips (overview ↔ endpoint scope), snap the active
  // lens to whatever is now first. Without this the user can land on a
  // page where their previously-selected tab is no longer in the leading
  // slot — the lens still works, it just looks misaligned with the new
  // priority order. Refs initialise to the current first lens so the
  // first render doesn't fire a synthetic reset.
  const firstLensValue = lensOptions[0][0];
  const prevFirstLensRef = useRef(firstLensValue);
  useEffect(() => {
    if (prevFirstLensRef.current !== firstLensValue) {
      // Always track the latest leading lens so re-enabling doesn't fire a
      // stale reset, but only actually snap when auto-reset is enabled.
      prevFirstLensRef.current = firstLensValue;
      if (autoResetLens) onLensChange?.(firstLensValue);
    }
  }, [firstLensValue, onLensChange, autoResetLens]);

  // Kernel tabs: per-scope-type pins. Subscribe to both the kernel
  // library AND the pin map so a saved/deleted kernel, or a fresh
  // pin/unpin, updates this view live without a remount.
  const scopeKey = useMemo(() => scopeKeyOf(scopePath), [scopePath]);
  const currentUser = useCurrentUser();
  const tenantId = currentUser?.currentTenant?.tenantId || null;
  const [allKernels, setAllKernels] = useState(() => listKernels(null));
  const [pinnedCommands, setPinnedCommands] = useState(() => listPinnedCommands(scopeKey, tenantId));
  useEffect(() => {
    const refresh = () => setAllKernels(listKernels(null));
    refresh();
    return subscribeKernels(refresh);
  }, [tenantId]);
  useEffect(() => {
    const refresh = () => setPinnedCommands(listPinnedCommands(scopeKey, tenantId));
    refresh();
    return subscribePinning(refresh);
  }, [scopeKey, tenantId]);
  // allKernels also acts as the kernel-store epoch: a late cloud catalog
  // notification must retry pins that missed their first async resolution.
  useEffect(() => {
    let cancelled = false;
    Promise.all(pinnedCommands.map((command) =>
      resolveKernelByCommandAsync(tenantId, command)))
      .catch((error) => {
        if (!cancelled) console.warn("[activity] pinned kernel load failed", error);
      });
    return () => { cancelled = true; };
  }, [pinnedCommands, tenantId, allKernels]);

  // MCP-backed kernels are only offered while their server is connected.
  // Re-derive the connected set both when connections change AND when the
  // tenant resolves — getCurrentTenantIdSync() is null until /v1/me lands,
  // and onConnectionsChanged doesn't fire on tenant arrival, so without the
  // tenant dep an MCP kernel would stay hidden on first paint until an
  // unrelated connection event.
  const [connectedServers, setConnectedServers] = useState(() => connectedServerIds(getCurrentTenantIdSync()));
  useEffect(() => {
    const refresh = () => setConnectedServers(connectedServerIds(getCurrentTenantIdSync()));
    refresh();
    return onConnectionsChanged(refresh);
  }, [tenantId]);

  // Resolve pinned commands to actual kernels (drops orphaned pins
  // pointing at deleted kernels). Order follows the pin list so the
  // user controls left-to-right tab order by add order.
  const kernelResolver = useMemo(
    () => kernelResolverForTenant(null),
    [allKernels],
  );
  const kernelTabs = useMemo(() => {
    return pinnedCommands
      .map((cmd) => kernelResolver.resolveByCommand(cmd)?.body)
      .filter(Boolean)
      // Defensive: a global kernel pinned on a deeper scope (e.g. a
      // legacy pin from before scope-gating) must not render there — it
      // can only show meaningful data on the overview.
      .filter((k) => canMountOnScope(k.scopeMode, scopeKey))
      // Hide kernels whose MCP server requirement isn't currently met.
      .filter((k) => kernelRequirementsMet(k, connectedServers));
  }, [kernelResolver, pinnedCommands, scopeKey, connectedServers]);

  // Stable params for kernels on the activity chart. Re-runs only
  // when scope/range actually change.
  const kernelParams = useMemo(() => ({
    scope: scopePath || [],
    range: range || null,
  }), [scopePath, range]);

  // Active lens prefixed `kernel:<command>` means "render this
  // kernel instead of the chart" — same canvas, different content.
  const activeKernelCommand = typeof activityLens === "string" && activityLens.startsWith("kernel:")
    ? activityLens.slice("kernel:".length)
    : null;
  const activeKernel = activeKernelCommand
    ? kernelTabs.find((k) => k.command === activeKernelCommand) || null
    : null;
  const activeResolvedDefinition = activeKernel
    ? kernelResolver.resolveByCommand(activeKernel.command)
    : null;
  // A pipeline query overrides the active lens — its chart fills the canvas.
  const showPipeline = !!(pipeline && pipeline.isPipeline);

  // If the active kernel tab disappears (unpinned elsewhere, kernel
  // deleted), snap back to the first lens so we don't render an
  // empty "kernel:foo" lens.
  useEffect(() => {
    if (!activeKernelCommand) return;
    const existsInTabs = kernelTabs.some(
      (kernel) => kernel.command === activeKernelCommand,
    );
    if (!existsInTabs) onLensChange?.(firstLensValue);
  }, [activeKernelCommand, kernelTabs, firstLensValue, onLensChange]);

  // Unpinned kernels available for the "+" picker — anything in the
  // library that isn't already pinned to this scope.
  const unpinnedKernels = useMemo(
    () => allKernels
      .filter((k) => !pinnedCommands.includes(k.command)
        && canMountOnScope(k.scopeMode, scopeKey)
        && kernelRequirementsMet(k, connectedServers),
      ),
    [allKernels, pinnedCommands, scopeKey, connectedServers],
  );
  const [addOpen, setAddOpen] = useState(false);
  const addBtnRef = useRef(null);
  return (
    <Sheet
      data-expand-target
      className={className}
      diagram
      expandable
      eyebrow={showPipeline ? "Query result" : eyebrow}
      title={showPipeline ? (pipeline.kernel?.title || "Query") : title}
      actions={
        <div className="flex items-center gap-[8px]">
          <ControlGroup
            className="justify-self-start"
            items={DATE_RANGE_PRESETS.map((p) => [p.id, p.label])}
            activeValue={range}
            onSelect={onRangeChange}
          />
          {pinnable ? <button
            type="button"
            onClick={onHomeClick}
            aria-pressed={homeActive}
            title={homeActive ? "Unpin this view" : (isHome ? "Open pinned view full screen" : "Pin this view to the rail")}
            aria-label={homeActive ? "Unpin this view" : "Pin this view"}
            className={[
              "grid h-[24px] w-[24px] place-items-center border border-[color:var(--line)] p-0 transition-colors [&_svg]:block [&_svg]:h-3 [&_svg]:w-3",
              homeActive
                ? "bg-[color:var(--ink)] text-[color:var(--bg)]"
                : "bg-[color:var(--bg)] [background-image:linear-gradient(var(--control-bg),var(--control-bg))] text-[color:var(--muted)] hover:text-[color:var(--ink)]",
            ].join(" ")}
          >
            <svg viewBox="-4 -4 32 32" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 17v5" />
              <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />
            </svg>
          </button> : null}
          {onToggleExpand ? (
            <button
              type="button"
              onClick={onToggleExpand}
              className="grid h-[24px] w-[24px] place-items-center border border-[color:var(--line)] bg-[color:var(--bg)] [background-image:linear-gradient(var(--control-bg),var(--control-bg))] p-0 text-[color:var(--muted)] transition-colors hover:text-[color:var(--ink)] [&_svg]:block [&_svg]:h-3 [&_svg]:w-3"
              aria-label={expanded ? "Collapse" : "Expand"}
            >
              {expanded ? (
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 12l8-8" /><path d="M12 12L4 4" />
                </svg>
              ) : (
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 2h4v4" /><path d="M6 14H2v-4" /><path d="M14 2l-4 4" /><path d="M2 14l4-4" />
                </svg>
              )}
            </button>
          ) : null}
        </div>
      }
    >
      <div className="mt-[6px] flex min-h-0 flex-1 flex-col">
        {/* -mb-[1px] pulls the chart panel up by 1px so the active
            tab's bottom mask (drawn at the strip's bottom edge,
            same color as --plot-bg) covers the chart panel's top
            border — without it you'd see a continuous horizontal
            line running under the active tab. */}
        <div className="-mb-[1px]">
          <TabStrip
            tabs={[
              // A pipeline query is a transient view: show it as its own "Query"
              // tab and un-highlight the lenses (clearing the `|>` restores them).
              ...(showPipeline ? [{ id: "__pipeline__", label: "Query", active: true, onSelect: () => {} }] : []),
              ...lensOptions.map(([value, label]) => ({
                id: value,
                label,
                active: !showPipeline && value === activityLens,
                onSelect: () => onLensChange(value),
              })),
              ...kernelTabs.map((k) => {
                const lensValue = `kernel:${k.command}`;
                // Defensive display strip for kernels saved before
                // the parser was fixed — their stored title still
                // has literal quote chars. New saves arrive clean.
                const rawLabel = k.title || k.command;
                const label = String(rawLabel).replace(/^['"](.*)['"]$/s, "$1");
                // Agentic kernels run an LLM on every refresh — flag them
                // on the tab with a distinct (amber) dot + tooltip so the
                // non-deterministic ones are visible where they execute,
                // not just in the editor. Deterministic kernels stay green.
                const isAgentic = k.agentic === true;
                return {
                  id: lensValue,
                  label,
                  title: isAgentic ? `/${k.command} · Agentic; runs an LLM on each refresh` : `/${k.command}`,
                  active: !showPipeline && lensValue === activityLens,
                  dot: isAgentic ? "bg-[color:var(--warn,#e0a23c)]" : "bg-[color:var(--green)]",
                  onSelect: () => onLensChange(lensValue),
                  onClose: () => {
                    // Unpin from this scope. If it was active, the
                    // snap-back effect above flips to firstLensValue.
                    unpinKernel(scopeKey, k.command, tenantId);
                  },
                };
              }),
            ]}
            actions={
              unpinnedKernels.length > 0 ? (
                // Same shape + bg as TabStrip's OverflowMenu button
                // so the pin affordance sits flush on the rail and
                // reads as part of the strip rather than a floating
                // primary action.
                <div className="relative z-[3] shrink-0">
                  <button
                    type="button"
                    ref={addBtnRef}
                    onClick={() => setAddOpen((v) => !v)}
                    title="Pin a kernel to this scope"
                    aria-label="Pin kernel"
                    className="flex min-h-[38px] w-[34px] items-center justify-center border border-b-0 border-[color:var(--line)] bg-[color:var(--bg)] [background-image:linear-gradient(var(--control-bg),var(--control-bg))] text-[color:var(--muted)] hover:text-[color:var(--ink)] cursor-pointer"
                  >
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" className="h-[12px] w-[12px]">
                      <path d="M8 3v10M3 8h10" />
                    </svg>
                  </button>
                  {addOpen ? (
                    <KernelPinPopover
                      kernels={unpinnedKernels}
                      onPin={(command) => {
                        pinKernel(scopeKey, command, tenantId);
                        onLensChange?.(`kernel:${command}`);
                        setAddOpen(false);
                      }}
                      onClose={() => setAddOpen(false)}
                      anchorRef={addBtnRef}
                    />
                  ) : null}
                </div>
              ) : null
            }
          />
        </div>
        <div
          className={`relative min-h-0 ${(activeKernel || showPipeline) ? "flex flex-col" : ""}`}
          style={
            expanded || fill
              ? { flex: "1", minHeight: 0 }
              : (activeKernel || showPipeline)
                ? {
                    // Same sizing rules as the regular chart canvas
                    // so a chart-kind kernel reads at the same scale
                    // as the lens charts the user is used to. The
                    // widget itself uses h-full to fill this slot.
                    aspectRatio: chartAspectRatio,
                    minHeight: chartMinHeight,
                    maxHeight: "calc(100vh - 300px)",
                  }
                : plotConfig.swimlane
                ? (() => {
                    // Fixed height so row size stays constant across scopes.
                    // Without this, an aspect-ratio'd container would rescale
                    // row heights based on the panel width / cluster count.
                    // Reserve extra space when the siblings note is present
                    // (cluster-scope path) so the canvas doesn't lose rows.
                    const h = SWIMLANE_BASE_HEIGHT + (siblingsOf ? SWIMLANE_NOTE_RESERVE : 0);
                    return { height: `${h}px`, minHeight: `${h}px` };
                  })()
                : {
                    aspectRatio: chartAspectRatio,
                    minHeight: chartMinHeight,
                    maxHeight: "calc(100vh - 300px)",
                  }
          }
        >
          {showPipeline ? (
            pipeline.kernel ? (
              // Render exactly like a regular kernel lens (same KernelWidget
              // chrome: border / panel-soft bg / padding) so every render kind
              // gets the same backing instead of bleeding onto the dotted canvas.
              <KernelWidget
                kernel={pipeline.kernel}
                params={kernelParams}
                showHeader={false}
              />
            ) : (
              <div className="h-full rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)]">
                <div className="grid h-full place-items-center p-[12px]">
                  <div className="grid gap-[4px] text-[11px] leading-[1.3]">
                    {(pipeline.errors.length ? pipeline.errors : [{ message: "Add an aggregate, such as count by day." }]).map((e, i) => (
                      <div key={i} className="flex items-baseline gap-[7px]">
                        <span className="uppercase tracking-[0.08em] text-[10px] font-semibold text-[color:var(--ink)]">error</span>
                        <span className="font-mono text-[color:var(--muted)]">{e.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )
          ) : activeKernel ? (
            <KernelWidget
              kernel={activeKernel}
              resolvedDefinition={activeResolvedDefinition}
              runContext={LEGACY_PIN_RUN_CONTEXT}
              params={kernelParams}
              showHeader={false}
            />
          ) : plotConfig.swimlane ? (
            <div className="flex h-full min-h-0 flex-col">
              {siblingsOf ? (
                <div className="flex h-[22px] min-h-[22px] shrink-0 items-center gap-x-[6px] overflow-hidden whitespace-nowrap rounded-t-[2px] border border-b-0 border-[color:var(--line)] bg-[color:var(--plot-bg)] px-[12px] text-[10px] leading-[1.1] text-[color:var(--muted)]">
                  <span className="shrink-0 tracking-[0.08em] uppercase text-[color:var(--muted-soft)]">
                    siblings of
                  </span>
                  <span className="min-w-0 truncate text-[color:var(--ink)]">{siblingsOf.name}</span>
                  <span className="shrink-0 text-[color:var(--muted-soft)]">·</span>
                  <span className="min-w-0 truncate text-[color:var(--muted)]">
                    topics with the most shared sessions
                  </span>
                </div>
              ) : null}
              <div className="relative flex-1 min-h-0">
                <ClusterSwimlane
                  scopePath={scopePath}
                  onHoverCluster={onHoverCluster}
                  onHoverCell={onHoverCell}
                  onClickCell={onClickCell}
                  onMetaChange={setSwimlaneMeta}
                  expanded={expanded || fill}
                  coherenceMode={coherenceMode}
                  className={`${siblingsOf ? "" : "rounded-t-[2px] "}border border-[color:var(--line)] bg-[color:var(--plot-bg)] text-[color:var(--plot-ink)]`}
                />
              </div>
              <SwimlaneLegend hoveredCell={hoveredCell} className="shrink-0 rounded-b-[2px]" />
            </div>
          ) : loading ? (
            // Refetch in flight (range or scope changed). The surface chart
            // keeps stale layers in state until new data lands, which reads
            // as "current" — show the placeholder instead so the user
            // never sees old numbers under a new range header.
            <div className="h-full rounded-[2px] border border-[color:var(--line)] bg-[color:var(--plot-bg)] text-[color:var(--plot-ink)]">
              <GridStatus state="loading" label="Loading activity…" />
            </div>
          ) : (
            <SurfaceChartWithLegend
              plotConfig={plotConfig}
              overlapNote={activityLens === "activity" ? "A session can appear under several harnesses. Category session counts overlap." : activityLens === "endpoints" ? "An endpoint can use several harnesses. Category endpoint counts overlap." : null}
              range={range}
              baselines={baselines}
              visibleBaselines={visibleBaselines}
              activeBaselineKeys={effectiveActiveBaselineKeys}
              onToggleBaseline={toggleBaseline}
            />
          )}
        </div>
      </div>
    </Sheet>
  );
}

// Inline-SVG sparkline for the legend hover breakdown. Deliberately NOT
// SurfaceChart (the sidebar's EntrySparkline renderer): SurfaceChart draws from
// a ResizeObserver-measured container that starts 0×0, so inside a portaled,
// shrink-to-fit tooltip row it never gets a width and renders blank — and even
// with a fixed wrapper it'd blank-then-draw each hover (async measure), which
// flickers as the cursor sweeps chips. A self-contained SVG (explicit viewBox,
// no measurement) draws instantly. Matches the house look — colored line over a
// faint area tint — and self-scales to its own max so a thin input band and a
// huge cache-read band are each legible.
function MiniSparkline({ series, color, width = 56, height = 14 }) {
  if (!Array.isArray(series) || series.length === 0) return null;
  // reduce, not Math.max(...series): spreading a long series as args throws
  // RangeError in V8/JSC. Floor at 1 so a degenerate all-zero band can't /0.
  const max = series.reduce((m, v) => (v > m ? v : m), 1);
  const n = series.length;
  const stepX = n > 1 ? width / (n - 1) : 0;
  const pts = series
    .map((v, i) => `${(i * stepX).toFixed(1)},${(height - (v / max) * (height - 1) - 0.5).toFixed(1)}`)
    .join(" ");
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="block shrink-0" aria-hidden="true">
      <polygon points={`0,${height} ${pts} ${width},${height}`} fill={color} fillOpacity="0.16" />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1" />
    </svg>
  );
}

// Token-volume legend hover: where THIS model's tokens went, over the window.
// One row per token type — dot · label · sparkline · total · % of the model's
// processed total — sorted so the dominant band (usually cache_read) reads
// first. reasoning (a subset of output) and partial coverage show as a note at
// the foot of THIS card, never folded into the total. Pure presenter over
// layer.byType.
function TokenLayerBreakdown({ layer, costUsd = null }) {
  const bt = layer.byType;
  if (!bt || !Array.isArray(bt.bands)) return null;
  const total = bt.bands.reduce((a, b) => a + (b.total || 0), 0);
  const rows = [...bt.bands].filter((b) => b.total > 0).sort((a, b) => b.total - a.total);
  const showReasoning = bt.reasoning > 0;
  const partial = bt.callsTotal > 0 && bt.callsWithTokens < bt.callsTotal;
  // Cache reuse — the shared "% cached" definition (tokenVolume.js), so this
  // matches the chip and the trace-list summary bar's card. Only shown when there's real cache
  // traffic (effectively Anthropic); a non-cache model has no cache bands, so
  // we don't render a misleading "0% reuse".
  const byId = Object.fromEntries(bt.bands.map((b) => [b.id, b.total]));
  const reuse = cacheReuseRate({ input: byId.input, cacheRead: byId.cache_read, cacheCreation: byId.cache_write });
  const showReuse = ((byId.cache_read || 0) + (byId.cache_write || 0)) > 0 && reuse != null;
  return (
    <div className="grid gap-[4px] min-w-[232px]">
      <div className="flex items-baseline justify-between gap-[12px]">
        <span className="font-medium text-[color:var(--ink)]">{layer.label}</span>
        <span className="text-[color:var(--muted)]">{formatCompact(total)} processed</span>
      </div>
      {showReuse && (
        <div className="flex items-baseline justify-between gap-[12px]">
          <span className="text-[color:var(--muted)]">Cache reuse</span>
          <span className="font-medium text-[color:var(--ink)]">{Math.round(reuse * 100)}%</span>
        </div>
      )}
      {costUsd != null && (
        <div className="flex items-baseline justify-between gap-[12px]">
          <span className="text-[color:var(--muted)]">Est. cost</span>
          <span className="font-medium text-[color:var(--ink)]">≈{formatUsd(costUsd)}</span>
        </div>
      )}
      {rows.map((b) => (
        <div key={b.id} className="flex items-center gap-[8px]">
          <span className="h-[6px] w-[6px] shrink-0 rounded-full" style={{ backgroundColor: b.color }} aria-hidden="true" />
          <span className="w-[64px] shrink-0 text-[color:var(--muted)]">{b.label}</span>
          <MiniSparkline series={b.series} color={b.color} />
          <span className="ml-auto tabular-nums text-[color:var(--ink)]">{formatCompact(b.total)}</span>
          <span className="w-[30px] text-right tabular-nums text-[color:var(--muted)]">
            {total > 0 ? Math.round((b.total / total) * 100) : 0}%
          </span>
        </div>
      ))}
      {(showReasoning || partial) && (
        <div className="mt-[1px] flex items-baseline justify-between gap-[12px] text-[color:var(--muted)]">
          <span>{showReasoning ? `${formatCompact(bt.reasoning)} reasoning` : ""}</span>
          <span>{partial ? `measured ${formatCompact(bt.callsWithTokens)}/${formatCompact(bt.callsTotal)}` : ""}</span>
        </div>
      )}
    </div>
  );
}

// Surface chart + interactive legend. Owns selection state so clicks
// only redraw this chart (the legend is the chart's own affordance —
// not a global filter). Selection defaults to "all on" and resets
// whenever the underlying layer set changes (lens or scope flip), so
// switching lens never lands the user on a stale subset.
// Token-volume legend's cost chip. Mirrors the trace-list summary bar's
// CostSummaryCell in WHICH state it shows — `suppressTotal` means no dollar
// figure on either surface, so the two can't disagree about whether an
// estimate is trustworthy. It states no basis: this chip sits on the
// token-volume chart, which already plots throughput, so the denominator is
// on screen. Exported for the render-branch tests
// (ActivityChart.tokenCost.test.jsx).
const CHIP_CLASS =
  "ml-auto shrink-0 whitespace-nowrap pl-[6px] text-[9px] leading-[1.08] text-[color:var(--muted)]";

export function CostLegendChip({ cost }) {
  if (cost?.priced && !cost.suppressTotal) {
    return (
      <span
        className={CHIP_CLASS}
        title={
          cost.hasUnpriced
            ? `Based on ${pricedPercent(cost)}% of processed tokens. Models without catalog rates are excluded. The estimate is lower than actual cost.`
            : "Estimated token cost"
        }
      >
        ≈<strong className="text-[color:var(--ink)]">{formatUsd(cost.total)}</strong>
        {cost.hasUnpriced ? ` (${pricedPercent(cost)}% priced)` : ""} est.
      </span>
    );
  }
  if (cost?.hasUnpriced) {
    return (
      <span
        className={CHIP_CLASS}
        title="Most tokens in this range have no catalog rate. There is not enough priced usage to estimate a total."
      >
        {100 - pricedPercent(cost)}% unpriced
      </span>
    );
  }
  return null;
}

function SurfaceChartWithLegend({
  plotConfig,
  overlapNote,
  range,
  baselines = [],
  visibleBaselines = [],
  activeBaselineKeys,
  onToggleBaseline,
}) {
  const layers = plotConfig.layers || [];
  const allIds = useMemo(
    () => layers.map((l) => String(l.id)),
    [layers],
  );
  const allKey = allIds.join("|");
  const [selectedIds, setSelectedIds] = useState(() => new Set(allIds));

  // Reset the selection when the layer set changes (different scope /
  // different lens). Without this, deselecting "Claude" then switching
  // scopes would carry the hidden state into a context where the user
  // would expect a fresh chart.
  useEffect(() => {
    setSelectedIds(new Set(allIds));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allKey]);

  const toggleLayer = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      // Empty selection isn't useful (chart goes blank). Re-select the
      // clicked layer instead so the user always sees something.
      if (next.size === 0) next.add(key);
      return next;
    });
  };

  const visibleLayers = useMemo(
    () => layers.filter((l) => selectedIds.has(String(l.id))),
    [layers, selectedIds],
  );

  const allSelected = visibleLayers.length === layers.length;

  const hasBaselineToggles = (baselines || []).length > 0;
  const hasLegend = layers.length > 0 || hasBaselineToggles;

  // Flex column so the legend takes its own row instead of overlaying
  // the chart canvas. Previously the legend was absolute-positioned at
  // bottom-[12px] which let the chart's line/fill draw underneath it —
  // the legend chip's --plot-bg pill masked some of the line but never
  // all of it (the chart's internal `padding` only insets the data area
  // 18px, so a 22-26px legend always stuck partway into live data).
  return (
    <div className="flex h-full min-h-0 flex-col">
      {overlapNote ? <p className="shrink-0 px-2 text-[10px] text-[color:var(--muted)]">{overlapNote}</p> : null}
      <div className="relative min-h-0 flex-1">
        <SurfaceChart
          mode={visibleLayers.length ? "stacked" : "line"}
          className={[
            "h-full border border-[color:var(--line)] bg-[color:var(--plot-bg)] text-[color:var(--plot-ink)]",
            hasLegend ? "rounded-t-[2px] border-b-0" : "rounded-[2px]",
          ].join(" ")}
          series={plotConfig.series}
          layers={visibleLayers}
          stroke={plotConfig.stroke}
          fill={plotConfig.fill}
          fillPatternColor={plotConfig.pattern}
          minLayerHeight={0}
          interactiveDots
          range={range}
          legendLayers={visibleLayers}
          referenceSeriesList={visibleBaselines}
        />
      </div>
      {hasLegend ? (
        <div className="flex shrink-0 items-center gap-x-[2px] overflow-hidden rounded-b-[2px] border border-[color:var(--line)] border-t-0 bg-[color:var(--plot-bg)] px-[4px] py-[3px]">
          {layers.map((layer) => {
            const id = String(layer.id);
            const on = selectedIds.has(id);
            // Token layers carry a per-model type breakdown — surface it on
            // hover via our tooltip (sparklines per token type). Other lenses
            // have no byType, so label is null and HoverTooltip is a no-op pass
            // through (the native title still gives the click-to-toggle hint).
            const breakdown = layer.byType
              ? <TokenLayerBreakdown layer={layer} costUsd={plotConfig.cost?.byModel?.[id]} />
              : null;
            return (
              <HoverTooltip key={id} placement="top" wide label={breakdown}>
                <button
                  type="button"
                  onClick={() => toggleLayer(id)}
                  title={breakdown ? undefined : (on ? `Hide ${layer.label}` : `Show ${layer.label}`)}
                  className={[
                    "inline-flex items-center gap-[3px] rounded-[2px] border px-[5px] py-[2px] text-[9px] leading-[1.08] transition-colors cursor-pointer",
                    on
                      ? "border-transparent text-[color:var(--muted)] hover:text-[color:var(--ink)]"
                      : "border-transparent text-[color:var(--muted-soft)] line-through opacity-50 hover:opacity-100 hover:text-[color:var(--muted)]",
                  ].join(" ")}
                >
                  <span
                    className={[
                      "h-[5px] w-[5px] rounded-[999px]",
                      on ? "" : "ring-1 ring-inset ring-[color:var(--line-strong)]",
                    ].join(" ")}
                    style={{ backgroundColor: on ? layer.color : "transparent" }}
                    aria-hidden="true"
                  />
                  {layer.label}
                </button>
              </HoverTooltip>
            );
          })}
          {!allSelected ? (
            <button
              type="button"
              onClick={() => setSelectedIds(new Set(allIds))}
              title="Show all"
              className="ml-[2px] inline-flex items-center rounded-[2px] border border-[color:var(--line)] px-[5px] py-[2px] text-[9px] leading-[1.08] tracking-[0.06em] uppercase text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)] cursor-pointer"
            >
              All
            </button>
          ) : null}
          {hasBaselineToggles ? (
            <span
              className={[
                "inline-flex items-center gap-x-[2px]",
                layers.length > 0 ? "ml-[6px] border-l border-l-[color:var(--line-soft)] pl-[6px]" : "",
              ].join(" ")}
            >
              {(baselines || []).map((b, i) => {
                const on = activeBaselineKeys.has(b.key);
                // Same visual ladder the canvas uses: deeper ancestor =
                // solid, others dashed. Keeps the chip readable as a
                // mini-key for the line on screen.
                const isParent = i === (baselines || []).length - 1;
                const dashed = !isParent;
                const title = on
                  ? `Hide ${b.label} baseline`
                  : `Show ${b.label} baseline`;
                return (
                  <button
                    key={b.key}
                    type="button"
                    onClick={() => onToggleBaseline(b.key)}
                    title={title}
                    aria-pressed={on}
                    className={[
                      "inline-flex cursor-pointer items-center gap-[3px] rounded-[2px] border px-[5px] py-[2px] text-[9px] leading-[1.08] transition-colors",
                      on
                        ? "border-transparent text-[color:var(--muted)] hover:text-[color:var(--ink)]"
                        : "border-transparent text-[color:var(--muted-soft)] line-through opacity-50 hover:opacity-100 hover:text-[color:var(--muted)]",
                    ].join(" ")}
                  >
                    <span
                      aria-hidden="true"
                      className="h-[1px] w-[10px] shrink-0"
                      style={{
                        backgroundColor: on && !dashed ? "var(--muted)" : "transparent",
                        backgroundImage:
                          on && dashed
                            ? "linear-gradient(to right, var(--muted) 50%, transparent 50%)"
                            : "none",
                        backgroundSize: on && dashed ? "4px 1px" : "auto",
                        backgroundRepeat: on && dashed ? "repeat-x" : "no-repeat",
                        outline: on ? "none" : "1px solid var(--line-strong)",
                        outlineOffset: "-1px",
                      }}
                    />
                    {b.label}
                  </button>
                );
              })}
            </span>
          ) : null}
          <CostLegendChip cost={plotConfig.cost} />
        </div>
      ) : null}
    </div>
  );
}

// Small popover anchored under the "+" button. Lists kernels not yet
// pinned to the current scope; picking one pins + activates it.
// Closes on outside click / Escape.
function KernelPinPopover({ kernels, onPin, onClose, anchorRef }) {
    const ref = useRef(null);
    useEffect(() => {
        const onDocPointer = (e) => {
            if (ref.current?.contains(e.target)) return;
            if (anchorRef?.current?.contains(e.target)) return;
            onClose?.();
        };
        const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
        document.addEventListener("mousedown", onDocPointer, true);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDocPointer, true);
            document.removeEventListener("keydown", onKey);
        };
    }, [onClose, anchorRef]);

    return (
        <div
            ref={ref}
            className="absolute right-0 top-[calc(100%+4px)] z-[20] min-w-[240px] max-w-[320px] rounded-[2px] border border-[color:var(--line-strong)] bg-[color:var(--bg)] shadow-lg"
            role="menu"
        >
            <div className="border-b border-[color:var(--line)] px-[10px] py-[6px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]">
                Pin kernel
            </div>
            <div className="max-h-[260px] overflow-y-auto">
                {kernels.map((k) => (
                    <button
                        key={k.id}
                        type="button"
                        onClick={() => onPin?.(k.command)}
                        role="menuitem"
                        className="flex w-full items-baseline gap-[8px] bg-transparent px-[10px] py-[6px] text-left text-[11px] leading-[1.3] text-[color:var(--muted)] hover:bg-[color:var(--control-bg)] hover:text-[color:var(--ink)] cursor-pointer"
                    >
                        <span className="shrink-0 whitespace-nowrap font-mono text-[color:var(--ink)]">
                            /{k.command}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-[color:var(--muted)]">
                            {k.title || ""}
                        </span>
                    </button>
                ))}
            </div>
        </div>
    );
}
